import React, { useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Link,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ReplyIcon from "@mui/icons-material/Reply";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import type { ProjectMessage, User, ProjectMessageReaction } from "./types";
import { SentAttachmentPreview } from "./AttachmentPreview";
import { ReactionPicker, ReactionButton } from "./ReactionPicker";

interface MessageItemProps {
  message: ProjectMessage;
  user?: User;
  currentUserId: string | null;
  isOwnMessage: boolean;
  onReply: (message: ProjectMessage) => void;
  onEdit: (message: ProjectMessage) => void;
  onDelete: (messageId: string) => void;
  onToggleReaction: (messageId: string, emoji: string) => void;
  users: Map<string, User>;
}

const formatTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const isYesterday =
    new Date(now.getTime() - 86400000).toDateString() === date.toDateString();

  const timeStr = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

  if (isToday) {
    return timeStr;
  } else if (isYesterday) {
    return `Yesterday ${timeStr}`;
  } else {
    return `${date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
    })} ${timeStr}`;
  }
};

const getUserName = (userId: string, users: Map<string, User>): string => {
  const user = users.get(userId);
  if (user) {
    return `${user.first_name} ${user.last_name}`.trim() || user.email || "User";
  }
  return "Unknown User";
};

const getUserInitials = (userId: string, users: Map<string, User>): string => {
  const user = users.get(userId);
  if (user) {
    const first = user.first_name?.[0] || "";
    const last = user.last_name?.[0] || "";
    return (first + last).toUpperCase() || "?";
  }
  return "?";
};

// Reactions display component
const ReactionsDisplay: React.FC<{
  reactions: ProjectMessageReaction[];
  currentUserId: string | null;
  onToggle: (emoji: string) => void;
  users: Map<string, User>;
}> = ({ reactions, currentUserId, onToggle, users }) => {
  if (!reactions || reactions.length === 0) return null;

  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
      {reactions.map((reaction) => {
        const userReacted = reaction.userIds.includes(currentUserId || "");
        const reactionUsers = reaction.userIds
          .map((id) => getUserName(id, users))
          .join(", ");

        return (
          <Tooltip key={reaction.emoji} title={reactionUsers} arrow>
            <Chip
              label={`${reaction.emoji} ${reaction.userIds.length}`}
              size="small"
              onClick={() => onToggle(reaction.emoji)}
              sx={{
                height: 24,
                fontSize: "0.75rem",
                cursor: "pointer",
                bgcolor: userReacted ? "primary.light" : "action.hover",
                color: userReacted ? "primary.contrastText" : "text.primary",
                "&:hover": {
                  bgcolor: userReacted ? "primary.main" : "action.selected",
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

export const MessageItem: React.FC<MessageItemProps> = ({
  message,
  user,
  currentUserId,
  isOwnMessage,
  onReply,
  onEdit,
  onDelete,
  onToggleReaction,
  users,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [reactionAnchor, setReactionAnchor] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleReactionClick = (event: React.MouseEvent<HTMLElement>) => {
    setReactionAnchor(event.currentTarget);
  };

  const handleReactionClose = () => {
    setReactionAnchor(null);
  };

  // Deleted message placeholder
  if (message.deleted) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: isOwnMessage ? "flex-end" : "flex-start",
          px: 2,
          py: 0.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontStyle: "italic",
            color: "text.disabled",
            bgcolor: "action.disabledBackground",
            px: 2,
            py: 1,
            borderRadius: 2,
          }}
        >
          This message was deleted
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="message-item"
      sx={{
        display: "flex",
        flexDirection: isOwnMessage ? "row-reverse" : "row",
        gap: 1,
        px: 2,
        py: 0.75,
        "&:hover": {
          bgcolor: "action.hover",
        },
      }}
    >
      {/* Avatar */}
      <Avatar
        sx={{
          width: 36,
          height: 36,
          fontSize: "0.875rem",
          bgcolor: isOwnMessage ? "primary.main" : "grey.500",
        }}
      >
        {getUserInitials(message.userId, users)}
      </Avatar>

      {/* Message content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: isOwnMessage ? "flex-end" : "flex-start",
          maxWidth: "75%",
        }}
      >
        {/* Reply reference */}
        {message.replyTo && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 0.5,
              pl: 1,
              borderLeft: "2px solid",
              borderColor: "primary.light",
              cursor: "pointer",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            <ReplyIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              Replying to{" "}
              <strong>{getUserName(message.replyTo.userId, users)}</strong>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 150,
              }}
            >
              {message.replyTo.deleted
                ? "(deleted)"
                : message.replyTo.content?.substring(0, 50)}
            </Typography>
          </Box>
        )}

        {/* Name and time header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 0.25,
          }}
        >
          <Typography variant="caption" fontWeight={600}>
            {getUserName(message.userId, users)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTime(message.createdAt)}
            {message.editedAt && " (edited)"}
          </Typography>
        </Box>

        {/* Message bubble */}
        <Box
          sx={{
            bgcolor: isOwnMessage ? "primary.main" : "grey.200",
            color: isOwnMessage ? "primary.contrastText" : "text.primary",
            px: 2,
            py: 1,
            borderRadius: 2,
            borderTopLeftRadius: isOwnMessage ? 16 : 4,
            borderTopRightRadius: isOwnMessage ? 4 : 16,
            position: "relative",
          }}
        >
          {/* Message text */}
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {message.content}
          </Typography>

          {/* Attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: message.content ? 1 : 0,
              }}
            >
              {message.attachments.map((attachment, index) => (
                <SentAttachmentPreview
                  key={index}
                  attachment={attachment}
                  compact
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Reactions */}
        <ReactionsDisplay
          reactions={message.reactions || []}
          currentUserId={currentUserId}
          onToggle={(emoji) => onToggleReaction(message._id, emoji)}
          users={users}
        />

        {/* Action buttons (show on hover) */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            mt: 0.5,
            opacity: 0,
            transition: "opacity 0.2s",
            ".message-item:hover &": {
              opacity: 1,
            },
          }}
        >
          <IconButton size="small" onClick={() => onReply(message)}>
            <ReplyIcon fontSize="small" />
          </IconButton>
          <ReactionButton onClick={handleReactionClick} />
          {isOwnMessage && (
            <>
              <IconButton size="small" onClick={() => onEdit(message)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleMenuOpen}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {/* Context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            onDelete(message._id);
            handleMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete Message
        </MenuItem>
      </Menu>

      {/* Reaction picker */}
      <ReactionPicker
        open={Boolean(reactionAnchor)}
        anchorEl={reactionAnchor}
        onClose={handleReactionClose}
        onSelect={(emoji) => onToggleReaction(message._id, emoji)}
      />
    </Box>
  );
};

export default MessageItem;
